<script>
import V4Button from 'dashboard/components-next/button/Button.vue';
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import SLAMetrics from './components/SLA/SLAMetrics.vue';
import SLAInfractionChart from './components/SLA/SLAInfractionChart.vue';
import SLATable from './components/SLA/SLATable.vue';
import SLAReportFilters from './components/SLA/SLAReportFilters.vue';
import { generateFileName } from 'dashboard/helper/downloadHelper';
import ReportHeader from './components/ReportHeader.vue';
import { LocalStorage } from 'shared/helpers/localStorage';
import { subDays } from 'date-fns';
import { getUnixStartOfDay, getUnixEndOfDay } from 'helpers/DateHelper';
import {
  parseReportURLParams,
  parseFilterURLParams,
} from './helpers/reportFilterHelper';

const defaultDateFilter = () => ({
  from: getUnixStartOfDay(subDays(new Date(), 6)),
  to: getUnixEndOfDay(new Date()),
});

const SLA_REPORTS_PAGE_SIZE_KEY = 'sla_reports_page_size';

export default {
  name: 'SLAReports',
  components: {
    V4Button,
    ReportHeader,
    SLAMetrics,
    SLAInfractionChart,
    SLATable,
    SLAReportFilters,
  },
  data() {
    return {
      pageNumber: 1,
      pageSize: Number(LocalStorage.get(SLA_REPORTS_PAGE_SIZE_KEY)) || 10,
      activeFilter: {
        from: 0,
        to: 0,
        assigned_agent_id: null,
        inbox_id: null,
        team_id: null,
        sla_policy_id: null,
        label_list: null,
        event_type: null,
      },
    };
  },
  computed: {
    ...mapGetters({
      slaReports: 'slaReports/getAll',
      slaMetrics: 'slaReports/getMetrics',
      slaMeta: 'slaReports/getMeta',
      uiFlags: 'slaReports/getUIFlags',
    }),
  },
  mounted() {
    this.activeFilter = this.getInitialFilter();
    this.$store.dispatch('agents/get');
    this.$store.dispatch('inboxes/get');
    this.$store.dispatch('teams/get');
    this.$store.dispatch('labels/get');
    this.$store.dispatch('sla/get');
    this.fetchSLAMetrics();
    this.fetchSLAReports();
  },
  methods: {
    getInitialFilter() {
      const dateParams = parseReportURLParams(this.$route.query);
      const filterParams = parseFilterURLParams(this.$route.query);
      const fallbackDateFilter = defaultDateFilter();

      return {
        from: dateParams.from || fallbackDateFilter.from,
        to: dateParams.to || fallbackDateFilter.to,
        assigned_agent_id: filterParams.agent_id,
        inbox_id: filterParams.inbox_id,
        team_id: filterParams.team_id,
        sla_policy_id: filterParams.sla_policy_id,
        label_list: filterParams.label,
        event_type: filterParams.event_type,
      };
    },
    fetchSLAReports({ pageNumber } = {}) {
      this.$store.dispatch('slaReports/get', {
        page: pageNumber || this.pageNumber,
        per_page: this.pageSize,
        ...this.activeFilter,
      });
    },
    fetchSLAMetrics() {
      this.$store.dispatch('slaReports/getMetrics', this.activeFilter);
    },
    onPageChange(pageNumber) {
      this.pageNumber = pageNumber;
      this.fetchSLAReports({ pageNumber });
    },
    onPageSizeChange(pageSize) {
      this.pageSize = pageSize;
      this.pageNumber = 1;
      LocalStorage.set(SLA_REPORTS_PAGE_SIZE_KEY, pageSize);
      this.fetchSLAReports({ pageNumber: 1 });
    },
    onFilterChange(params) {
      this.activeFilter = params;
      this.pageNumber = 1;
      this.fetchSLAReports();
      this.fetchSLAMetrics();
    },
    downloadReports() {
      const type = 'sla';
      try {
        this.$store.dispatch('slaReports/download', {
          fileName: generateFileName({ type, to: this.activeFilter.to }),
          ...this.activeFilter,
        });
      } catch (error) {
        useAlert(this.$t('SLA_REPORTS.DOWNLOAD_FAILED'));
      }
    },
  },
};
</script>

<template>
  <ReportHeader :header-title="$t('SLA_REPORTS.HEADER')">
    <V4Button
      :label="$t('SLA_REPORTS.DOWNLOAD_SLA_REPORTS')"
      icon="i-ph-download-simple"
      size="sm"
      @click="downloadReports"
    />
  </ReportHeader>
  <div class="flex flex-col flex-1 gap-6">
    <SLAReportFilters @filter-change="onFilterChange" />
    <SLAMetrics
      :hit-rate="slaMetrics.hitRate"
      :no-of-breaches="slaMetrics.numberOfSLAMisses"
      :no-of-conversations="slaMetrics.numberOfConversations"
      :is-loading="uiFlags.isFetchingMetrics"
    />
    <SLAInfractionChart
      :total-conversations="slaMetrics.numberOfConversations"
      :total-breaches="slaMetrics.numberOfSLAMisses"
      :first-response-breaches="slaMetrics.firstResponseMisses"
      :resolution-breaches="slaMetrics.resolutionMisses"
      :is-loading="uiFlags.isFetchingMetrics"
    />
    <SLATable
      :sla-reports="slaReports"
      :is-loading="uiFlags.isFetching"
      :current-page="Number(slaMeta.currentPage)"
      :total-count="Number(slaMeta.count)"
      :page-size="pageSize"
      @page-change="onPageChange"
      @page-size-change="onPageSizeChange"
    />
  </div>
</template>
